import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { useQuery } from "@apollo/client"
import { DataLoading, Theme } from "@pga/pga-component-library"

import CurriculumTracker from "../CurriculumTracker"
import { ButtonFw } from "../../Button"
import { urlFor, getCurrentProgressLevelCode } from "./CurriculumMessage/utils"
import CurriculumMessage from "./CurriculumMessage"
import {
  MembershipJourneyProvider,
  useMembershipJourneyContext,
} from "../MembershipJourney/MembershipJourneyContext"
import {
  getCurrentLevelReapplyStatus,
  resolveAcceptableProgressStanding,
} from "../../../utils/resolveAcceptableProgressStanding"
import {
  shouldShowCurriculumReapplyCta,
  shouldSuppressAdvancementCtas,
  shouldUseTerminatedGptpCopy,
} from "../../../utils/shouldSuppressAdvancementCtas"
import { GPTP_MESSAGE_VARIANT } from "../MembershipJourney/utils/suspensionUtils"
import { flags, useFeatureFlags } from "../../../lib/feature-flags"
import { isAffiliate } from "../../utils"
import { COACH_TOOLS_API } from "../../../apolloClient"
import {
  MEMBER_APPLICATIONS_URL,
  redirectToMemberApplications,
} from "../../../lib/ptmApplicationsRedirect"
import { ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY } from "../../queries/associateApplicationEligibility"
import { PTM_MEMBERSHIP_APPLICATIONS_QUERY } from "../../../utils/prospect/withPtmApplicationsQuery"
import {
  pickLatestApplicationForType,
  PTM_APPLICATION_TYPES,
} from "../../../utils/prospect/ptmApplicationStatus"
import {
  areReapplyPathApplicationsReady,
  reapplyPathActionState,
} from "../../../utils/reapplyPathOffer"
import { shouldShowAffiliateAssociateApplicationLink } from "../../../utils/affiliateAssociateApplicationLink"
import PtmApplicationStatus from "../../PtmApplicationStatus"
import BecomeAssociateModal from "./BecomeAssociateModal"

const Box = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 5px 20px 0 20px;
  width: 100%;
  height: 100%;
  border-right: 2px solid ${Theme.colors.darkgrey};

  @media (max-width: 767px) {
    padding-top: 35px;
  }
`

export const ButtonsBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
`

const LevelTrackBox = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
`

export const TextWrapper = styled.div`
  padding: 0 15px;
`

export const Loading = () => (
  <Box>
    <br />
    <DataLoading h={25} />
    <br />
    <DataLoading h={60} />
    <ButtonsBox>
      <DataLoading h={40} w={150} />
      <DataLoading h={40} w={150} />
    </ButtonsBox>
  </Box>
)

const CourseCurriculum = () => {
  const [isBecomeAssociateModalOpen, setIsBecomeAssociateModalOpen] =
    useState(false)
  const parentContext = useMembershipJourneyContext()
  const { me, pgmProgress } = parentContext
  const affiliateViewer = isAffiliate(me)
  const [isTerminatedNoticeEnabled = false] =
    useFeatureFlags([flags.FLAG_FEAT_TERMINATED_NOTICE]) ?? []
  const progressStanding = resolveAcceptableProgressStanding({
    me,
    pgmProgress,
  })
  const aptContext = {
    ...parentContext,
    progressStanding,
    gptpMessageVariant: shouldUseTerminatedGptpCopy({
      isTerminatedNoticeEnabled,
      standing: progressStanding,
    })
      ? GPTP_MESSAGE_VARIANT.TERMINATED
      : GPTP_MESSAGE_VARIANT.SUSPENDED,
  }
  const suppressLegacyBecomeAssociate = shouldSuppressAdvancementCtas({
    standing: progressStanding,
    isTerminatedNoticeEnabled,
  })

  const skipAssociateCtaQueries =
    !affiliateViewer || suppressLegacyBecomeAssociate
  const { data: eligibilityData, loading: eligibilityLoading } = useQuery(
    ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY,
    {
      context: { api: COACH_TOOLS_API },
      errorPolicy: "all",
      skip: skipAssociateCtaQueries,
    },
  )
  const {
    data: ptmApplicationsData,
    loading: ptmApplicationsLoading,
    error: ptmApplicationsError,
  } = useQuery(PTM_MEMBERSHIP_APPLICATIONS_QUERY, {
    context: { api: COACH_TOOLS_API },
    skip: skipAssociateCtaQueries,
  })

  const associateCtaQueriesReady =
    skipAssociateCtaQueries ||
    (!eligibilityLoading &&
      areReapplyPathApplicationsReady({
        shouldFetch: true,
        loading: ptmApplicationsLoading,
        data: ptmApplicationsData,
        error: ptmApplicationsError,
      }))
  const membershipApplications = ptmApplicationsError
    ? []
    : ptmApplicationsData?.membershipApplications || []
  const associateApplication = pickLatestApplicationForType(
    membershipApplications,
    PTM_APPLICATION_TYPES.ASSOCIATE,
  )
  const associateAction = reapplyPathActionState(
    associateApplication,
    shouldShowAffiliateAssociateApplicationLink({
      pgmProgress,
      eligibilityEligible:
        eligibilityData?.associateApplicationEligibility?.eligible === true,
      standing: progressStanding,
    }),
  )
  const showBecomeAssociateButton =
    !skipAssociateCtaQueries &&
    associateCtaQueriesReady &&
    associateAction.showApplyCta &&
    !associateAction.isResume
  const showResumeAssociateButton =
    !skipAssociateCtaQueries &&
    associateCtaQueriesReady &&
    associateAction.showApplyCta &&
    associateAction.isResume
  const showAssociateApplicationStatus =
    !skipAssociateCtaQueries &&
    associateCtaQueriesReady &&
    associateAction.showStatus
  const showReapplyButton = shouldShowCurriculumReapplyCta({
    isTerminatedNoticeEnabled,
    standing: progressStanding,
    reapplyStatus: getCurrentLevelReapplyStatus(pgmProgress),
  })

  useEffect(() => {
    if (!showBecomeAssociateButton) {
      setIsBecomeAssociateModalOpen(false)
    }
  }, [showBecomeAssociateButton])

  const current = getCurrentProgressLevelCode(me)
  const transcriptUrl = urlFor("transcript", me)
  const portalUrl = urlFor("portal", me)
  return (
    <MembershipJourneyProvider value={aptContext}>
      <Box>
        <TextWrapper>{CurriculumMessage(me)}</TextWrapper>
        <LevelTrackBox>
          <CurriculumTracker current={current} me={me} />
        </LevelTrackBox>
        <ButtonsBox>
          {showReapplyButton && (
            <a
              className="w-100 p-3"
              href={MEMBER_APPLICATIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ButtonFw>Reapply</ButtonFw>
            </a>
          )}
          {showAssociateApplicationStatus && (
            <div className="w-100 p-3">
              <PtmApplicationStatus
                status={associateApplication.status}
                date={
                  associateAction.showDeniedStatus
                    ? associateApplication.updatedAt
                    : undefined
                }
                applicationType={PTM_APPLICATION_TYPES.ASSOCIATE}
              />
            </div>
          )}
          {(showBecomeAssociateButton || showResumeAssociateButton) && (
            <div className="w-100 p-3">
              <ButtonFw
                onClick={
                  showResumeAssociateButton
                    ? redirectToMemberApplications
                    : () => setIsBecomeAssociateModalOpen(true)
                }
              >
                {showResumeAssociateButton
                  ? "Resume Associate"
                  : "Become Associate"}
              </ButtonFw>
            </div>
          )}
          {transcriptUrl && (
            <a
              className="w-100 p-3"
              href={transcriptUrl}
              rel="noopener noreferrer"
            >
              <ButtonFw outline>VIEW TRANSCRIPT</ButtonFw>
            </a>
          )}
          {portalUrl && (
            <a
              className="w-100 p-3"
              href={portalUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ButtonFw outline={showReapplyButton}>EDUCATION PORTAL</ButtonFw>
            </a>
          )}
        </ButtonsBox>
        <BecomeAssociateModal
          isOpen={isBecomeAssociateModalOpen}
          onClose={() => setIsBecomeAssociateModalOpen(false)}
          onApply={redirectToMemberApplications}
        />
      </Box>
    </MembershipJourneyProvider>
  )
}

export default CourseCurriculum
